// Story: the week in sentences (compared with the week before), the milestones wall, and the
// opt-in for the Monday digest email (settled question 5).
import { api, missing } from "../../api.ts";
import { useLoad } from "../../data/hooks.ts";
import { milestoneLabel, MILESTONES, type Milestone } from "../../data/stats.ts";
import { Card, Failed, Loading, useAction } from "../../ui.tsx";
import { ProductFrame, useProject } from "./ProjectPage.tsx";

export function DigestToggle() {
  const q = useLoad("digest", () => api<{ optIn: boolean }>("/v1/stats/digest").catch((e) => (missing(e) ? null : Promise.reject(e))), 300_000);
  const act = useAction();
  if (q.data === null) return <p className="small muted">The weekly digest arrives with the next Estra Cloud update.</p>;
  if (!q.data) return <Loading />;
  return (
    <div className="row">
      <label className="toggle"><input type="checkbox" checked={q.data.optIn} disabled={act.busy} onChange={(e) => act.run(async () => { await api("/v1/stats/digest", { optIn: e.target.checked }); q.reload(); })} />Email me a digest every Monday</label>
      {act.note}
    </div>
  );
}

export function Story() {
  const { stats } = useProject();
  const s = stats.data;
  const reached = new Map((s?.milestones ?? []).map((m) => [m.key, m]));
  return (
    <ProductFrame product="story">
      {stats.error ? <Failed error={stats.error} retry={stats.reload} /> : !s ? <Loading /> : (
        <div className="stack" style={{ gap: 20 }}>
          <Card title="This week" sub="Deterministic sentences from your numbers, this week against the last.">
            {s.story.length ? (
              <ul className="story">{s.story.map((l, i) => <li key={i}><span className={`tone ${l.tone}`}>{l.tone === "good" ? "▲" : l.tone === "bad" ? "▼" : "•"}</span><span>{l.text}</span><a className="go" href={l.href}>Look →</a></li>)}</ul>
            ) : <p className="muted">{s.fallback ? "Your story starts once your backend is updated for the new console." : "A story needs a week of numbers. Check back in a few days."}</p>}
          </Card>
          <Card title="Milestones" sub="Firsts and thresholds, each marked on your charts with a ◆ on the day it happened.">
            <div className="wall">
              {Object.entries(MILESTONES).map(([key, def]) => {
                const m: Milestone | undefined = reached.get(key);
                return (
                  <div key={key} className={`badge-card${m ? "" : " locked"}`}>
                    <span className="diamond" aria-hidden="true" />
                    <strong>{m ? milestoneLabel(m) : def.label}</strong>
                    <span className="small muted">{m ? new Date(m.reachedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : def.hint}</span>
                  </div>
                );
              })}
            </div>
          </Card>
          <Card title="Weekly digest" sub="Your projects' numbers with deltas, the top story lines and new milestones, in one email on Monday mornings.">
            <DigestToggle />
          </Card>
        </div>
      )}
    </ProductFrame>
  );
}
